import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import GetAppIcon from "@mui/icons-material/GetApp";
import { openSnackbar } from "store/slices/snackbar";
import SubCard from "ui-component/cards/SubCard";
import moment from "moment";

const PaymentTransactionsCard = ({ RequestId, rendomRequestId }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);
  const userId = localStorage.getItem("userId");

  const handleDownloadReceipt = async (receiptId) => {
    if (!receiptId) {
      dispatch(
        openSnackbar({
          open: true,
          message: "Receipt ID is missing",
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        }),
      );

      return;
    }

    setDownloadingReceiptId(receiptId);

    try {
      const response = await axios.get(
        `/v1/pdf/generate/receipt?receiptId=${receiptId}`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem(
              "serviceToken",
            )}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
            ),
          },
        },
      );
      const receiptUrl = response?.data?.data;
      if (typeof receiptUrl !== "string" || !receiptUrl) {
        throw new Error("Receipt URL was not returned by the server");
      }

      const link = document.createElement("a");
      link.href = receiptUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message:
            error?.response?.data?.Msg ||
            error?.message ||
            "Unable to download receipt",
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        }),
      );
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get("v1/payment/get/transactions", {
        params: { userId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
          ),
        },
      });

      const data = response?.data?.data?.body ?? response?.data?.data ?? [];
      const rows = Array.isArray(data)
        ? data
        : data?.transactions ?? data?.content ?? [];

      setTransactions(Array.isArray(rows) ? rows : []);
    } catch (fetchError) {
      setError(
        fetchError?.response?.data?.message ??
          "Unable to load payment transactions.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);

    if (transactions.length === 0 && !loading) {
      fetchTransactions();
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleClose = () => setOpen(false);

  return (
    <>
      <SubCard
        sx={{ mt: 1 }}
        title="Payment Transactions"
        secondary={
          <IconButton
            aria-label="Show all payment transactions"
            onClick={handleOpen}
            size="small"
          >
            <AspectRatioIcon />
          </IconButton>
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : transactions.length === 0 ? (
          <Typography sx={{ py: 1 }}>No payment transactions found.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Payment Type</TableCell>

                  <TableCell align="center">Amount</TableCell>
                  <TableCell align="center">Payment Date</TableCell>
                  <TableCell align="center">Download Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow
                    key={transaction.id ?? transaction.transactionId ?? index}
                  >
                    <TableCell align="center">
                      {transaction.feeHeads?.applicableFor ?? "—"}
                    </TableCell>

                    <TableCell align="center">
                      {transaction.amountPaid ?? transaction.amountPaid ?? "—"}
                    </TableCell>
                    <TableCell align="center">
                      {transaction.paymentDate ??
                        transaction.createdAt ??
                        transaction.date ??
                        "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Download receipt">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Download receipt"
                            disabled={
                              !transaction.receiptId ||
                              downloadingReceiptId === transaction.receiptId
                            }
                            onClick={() =>
                              handleDownloadReceipt(transaction.receiptId)
                            }
                          >
                            {downloadingReceiptId === transaction.receiptId ? (
                              <CircularProgress size={18} />
                            ) : (
                              <GetAppIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SubCard>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          Payment Transactions
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={fetchTransactions}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : transactions.length === 0 ? (
            <Typography sx={{ py: 2 }}>
              No payment transactions found.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    {/* <TableCell>Transaction ID</TableCell> */}
                    <TableCell>Order ID</TableCell>
                    <TableCell>Receipt No.</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell align="center">Download Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction, index) => (
                    <TableRow
                      key={transaction.id ?? transaction.transactionId ?? index}
                    >
                      <TableCell>
                        {transaction.feeHeads?.applicableFor ?? "—"}
                      </TableCell>
                      {/* <TableCell>
                        {transaction.transactionId ?? transaction.id ?? "—"}
                      </TableCell> */}
                      <TableCell>{transaction.orderId ?? "—"}</TableCell>
                      <TableCell>{transaction.receiptNo ?? "—"}</TableCell>
                      <TableCell>
                        {transaction.amountPaid ??
                          transaction.amountPaid ??
                          "—"}
                      </TableCell>
                      <TableCell>{transaction.paymentStatus ?? "—"}</TableCell>
                      <TableCell>
                        {moment(
                          transaction.paymentDate ??
                            transaction.createdAt ??
                            transaction.date,
                        )?.format("DD-MM-YYYY") || "—"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Download receipt">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Download receipt"
                              disabled={
                                !transaction.receiptId ||
                                downloadingReceiptId === transaction.receiptId
                              }
                              onClick={() =>
                                handleDownloadReceipt(transaction.receiptId)
                              }
                            >
                              {downloadingReceiptId ===
                              transaction.receiptId ? (
                                <CircularProgress size={18} />
                              ) : (
                                <GetAppIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentTransactionsCard;
